import { Fragment, ReactNode } from "react";

interface FormattedTextProps {
  text?: string | null;
}

// Block-level tokens: only recognized at the start of a line, same as real
// markdown (a "#" or "*" in the middle of a sentence is just punctuation).
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const UNORDERED_RE = /^[-*]\s+(.*)$/;
const ORDERED_RE = /^(\d+)\.\s+(.*)$/;

// Inline tokens: recognized anywhere in a line.
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  return line.split(INLINE_RE).map((part, partIndex) => {
    const key = `${keyPrefix}-${partIndex}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 3) {
      return <strong key={key}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 1) {
      return (
        <code
          key={key}
          className="px-1 py-0.5 rounded text-[0.9em] font-mono"
          style={{ background: "rgba(127,127,127,0.18)" }}
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

// Renders the lightweight markdown subset that shows up in free-text fields
// across the app: bold/inline-code produced inline, plus headings and list
// items produced either by htmlToMarkdown (rich paste) or typed by hand.
// Not a full markdown parser — just enough to stop the raw ##, *, ` markers
// from showing up literally.
export function FormattedText({ text }: FormattedTextProps) {
  if (!text) return null;

  return (
    <>
      {text.split("\n").map((line, lineIndex) => {
        const br = lineIndex > 0 ? <br key={`br-${lineIndex}`} /> : null;

        const heading = line.match(HEADING_RE);
        if (heading) {
          const level = heading[1].length;
          return (
            <Fragment key={lineIndex}>
              {br}
              <span className={`block mt-1.5 mb-0.5 ${level <= 2 ? "font-bold" : "font-semibold"}`}>
                {renderInline(heading[2], `h${lineIndex}`)}
              </span>
            </Fragment>
          );
        }

        const unordered = line.match(UNORDERED_RE);
        if (unordered) {
          return (
            <Fragment key={lineIndex}>
              {br}
              <span className="block pl-4">• {renderInline(unordered[1], `ul${lineIndex}`)}</span>
            </Fragment>
          );
        }

        const ordered = line.match(ORDERED_RE);
        if (ordered) {
          return (
            <Fragment key={lineIndex}>
              {br}
              <span className="block pl-4">
                {ordered[1]}. {renderInline(ordered[2], `ol${lineIndex}`)}
              </span>
            </Fragment>
          );
        }

        return (
          <Fragment key={lineIndex}>
            {br}
            {renderInline(line, `p${lineIndex}`)}
          </Fragment>
        );
      })}
    </>
  );
}
